import { useDispatch } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import CurrentUserQuery from '../../graphql/queries/system/CurrentUserQuery.graphql';
import { appActions } from '../../state/modules/app';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { useCallback, useEffect, useMemo } from 'react';
import { Analytics } from '../../mixpanel';
import * as Sentry from '@sentry/react';
import { ICurrentUserQueryResult, ICurrentUserQueryVariables, IFullUserDetails } from '../../graphql/schema/p4types';
import { EntityType } from '../../@types/entityTypes';

/**
 * Fetch the currently logged in user (FullUserDetails) and set it into the redux store.
 *
 * On mount, will fetch the user & trigger a route reset (To initialise them the first time you open a P4 window).
 *
 * @param resetRoutes - Function to reset available routes (Which screens the user can access).
 * @param logout - Function to call on logout of a user (Used by this function to logout if user query fails).
 */
export const useCurrentUser = ({
  logout,
  resetRoutes,
}: {
  logout: () => void;
  resetRoutes: (user: IFullUserDetails) => void;
}) => {
  const dispatch = useDispatch();

  const [fetchUser, fetchUserQueryResult] = useLazyQuery<ICurrentUserQueryResult, ICurrentUserQueryVariables>(
    CurrentUserQuery,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true, // makes refetch trigger onCompleted
      onError: (error) => {
        //Unable to query for current user, logout.
        console.error('Unable to query for current user, logging out.');
        logout();
        handleComponentQueryError(error, { dispatch, name: 'User', entity: {} as EntityType });
      },
    }
  );

  const trackUserLogin = useCallback((currentUser) => {
    const lastsignin = new Date();
    const domain = currentUser.systemUser.email.indexOf('@');
    const userId = `user${currentUser.systemUser.id}${currentUser.systemUser.email.substr(domain)}`;
    Analytics.identify(userId);
    Analytics.set({ $email: userId });
    Analytics.set({ lastSignedIn: `${lastsignin.toISOString()}` });
    Analytics.setOnce({ firstSignedIn: `${lastsignin.toISOString()}` });
    Analytics.increment('signInRequests');
    Analytics.register({
      company: currentUser?.currentCompany?.name,
    });
    Analytics.track('User Signed In');

    Sentry.setUser({
      id: currentUser.systemUser.id,
      username: currentUser.systemUser.username,
      // eslint-disable-next-line camelcase
      ip_address: '{{auto}}',
    });
  }, []);

  const fetchUserAndUpdateRedux = useCallback(
    async (options?: Parameters<typeof fetchUser>[0]) => {
      const data = await fetchUser(options);
      const currentUser = data?.data?.currentUser as IFullUserDetails;
      dispatch(appActions.updateUser(currentUser));
      trackUserLogin(currentUser);
      return data;
    },
    [dispatch, fetchUser, trackUserLogin]
  );

  const fetchUserAndResetRoutes = useCallback(
    (shouldResetRoutes: boolean) => {
      return fetchUserAndUpdateRedux().then((data) => {
        if (shouldResetRoutes) {
          const currentUser = data?.data?.currentUser as IFullUserDetails;
          resetRoutes(currentUser);
        }
        return data;
      });
    },
    [fetchUserAndUpdateRedux, resetRoutes]
  );

  useEffect(() => {
    fetchUserAndResetRoutes(true).then(() => {
      // Do Nothing.
    });
    // didMount - Dependencies permitted since they should not change after initial mount.
  }, [fetchUserAndResetRoutes]);

  return useMemo(
    () => ({
      loading: fetchUserQueryResult.loading,
      refetch: fetchUserAndResetRoutes,
      data: fetchUserQueryResult.data,
    }),
    [fetchUserAndResetRoutes, fetchUserQueryResult.data, fetchUserQueryResult.loading]
  );
};
